import { http } from './http';
import type {
  ApiResponse,
  AttendanceLocation,
  AttendanceRecord,
  MonthlyReport,
  PaginatedResult,
  UserReport,
} from '@/types/api';

export interface PunchInput {
  location: AttendanceLocation;
  note?: string;
}

export interface MyListQuery {
  page?: number;
  limit?: number;
  from?: string;
  to?: string;
}

export interface AdminListQuery {
  page?: number;
  limit?: number;
  search?: string;
  user?: string;
  from?: string;
  to?: string;
  status?: 'present' | 'absent' | 'incomplete' | 'late';
}

export interface MonthlyReportQuery {
  month: string;
  user?: string;
}

export interface UserReportQuery {
  from: string;
  to: string;
}

export interface ExportQuery {
  format: 'xlsx' | 'pdf';
  search?: string;
  user?: string;
  from?: string;
  to?: string;
  status?: 'present' | 'absent' | 'incomplete' | 'late';
  // When set, the export mirrors the Monthly Summary view (aggregated totals).
  month?: string;
}

export interface AdminPunchInput {
  at: string;
  location: AttendanceLocation;
  note?: string;
}

export interface AdminUpsertInput {
  user: string;
  date: string;
  checkIn?: AdminPunchInput | null;
  checkOut?: AdminPunchInput | null;
}

export interface AdminUpdateInput {
  checkIn?: AdminPunchInput | null;
  checkOut?: AdminPunchInput | null;
}

interface TodayResponse {
  date: string;
  record: AttendanceRecord | null;
}

export const attendanceService = {
  async getToday(): Promise<TodayResponse> {
    const res = await http.get<ApiResponse<TodayResponse>>('/attendance/me/today');
    return res.data.data;
  },
  async listMine(query: MyListQuery = {}): Promise<PaginatedResult<AttendanceRecord>> {
    const res = await http.get<ApiResponse<PaginatedResult<AttendanceRecord>>>(
      '/attendance/me',
      { params: query },
    );
    return res.data.data;
  },
  async checkIn(input: PunchInput): Promise<AttendanceRecord> {
    const res = await http.post<ApiResponse<AttendanceRecord>>(
      '/attendance/check-in',
      input,
    );
    return res.data.data;
  },
  async checkOut(input: PunchInput): Promise<AttendanceRecord> {
    const res = await http.post<ApiResponse<AttendanceRecord>>(
      '/attendance/check-out',
      input,
    );
    return res.data.data;
  },
  async listAdmin(query: AdminListQuery = {}): Promise<PaginatedResult<AttendanceRecord>> {
    const res = await http.get<ApiResponse<PaginatedResult<AttendanceRecord>>>(
      '/attendance',
      { params: query },
    );
    return res.data.data;
  },
  async monthlyReport(query: MonthlyReportQuery): Promise<MonthlyReport> {
    const res = await http.get<ApiResponse<MonthlyReport>>(
      '/attendance/report/monthly',
      { params: query },
    );
    return res.data.data;
  },
  async userReport(userId: string, query: UserReportQuery): Promise<UserReport> {
    const res = await http.get<ApiResponse<UserReport>>(
      `/attendance/report/user/${userId}`,
      { params: query },
    );
    return res.data.data;
  },
  async download(query: ExportQuery): Promise<Blob> {
    const res = await http.get('/attendance/export', {
      params: query,
      responseType: 'blob',
    });
    return res.data as Blob;
  },
  async adminUpsert(input: AdminUpsertInput): Promise<AttendanceRecord> {
    const res = await http.post<ApiResponse<AttendanceRecord>>(
      '/attendance/admin',
      input,
    );
    return res.data.data;
  },
  async adminUpdate(id: string, input: AdminUpdateInput): Promise<AttendanceRecord> {
    const res = await http.patch<ApiResponse<AttendanceRecord>>(
      `/attendance/${id}`,
      input,
    );
    return res.data.data;
  },
  async adminDelete(id: string): Promise<void> {
    await http.delete<ApiResponse<{ id: string }>>(`/attendance/${id}`);
  },
};
